import PropTypes from 'prop-types';
import React from 'react';
import theme from '@/common/theme';
import { toSentenceCase } from '@/utils/utils';
import Styled from './status-indicator-style';

// STATUS COLORS
const statusEnum = {
  ACTIVE: 'active',
  ACCEPTED: 'accepted',
  ACTIVATED: 'activated',
  ACTIVATION_READY: 'activation_ready',
  NEW: 'new',
  IN_PROGRESS: 'inprogress',
  WAITING_CUSTOMER: 'waitingcustomer',
  WAITING_SUPPLIER: 'waitingsupplier',
  RESOLVED: 'resolved',
  TEST_READY: 'test_ready',
  INVENTORY: 'inventory',
  RETIRED: 'retired',
  ESCALATED: 'escalated',
  ARCHIVE: 'archive',
  SUSPENDED: 'suspended',
  ARCHIVED: 'archived',
  DEACTIVATED: 'deactivated',
  DRAFT: 'draft',
  SENT: 'sent',
  DECLINED: 'declined',
  VERIFIED: 'verified',
  UNVERIFIED: 'unverified',
  INVALID: 'invalid',
  INACTIVE: 'inactive',
  COMPLETED: 'completed',
  PENDING: 'pending',
  FAILED: 'failed',
  IS_SUPPLIER: 'supplier',
  IS_DEALER: 'dealer',
  QUOTED: 'quoted',
  REJECTED: 'rejected',
  SOLD: 'sold',
};

/**
 * Get the text color for a status.
 *
 * @param string status.
 *
 * @returns string color.
 */

const getStatusColorText = (status) => {
  switch (status) {
    case statusEnum.ACTIVE:
    case statusEnum.ACTIVATED:
    case statusEnum.ACCEPTED:
    case statusEnum.ACTIVATION_READY:
    case statusEnum.VERIFIED:
    case statusEnum.COMPLETED:
    case statusEnum.SOLD:
      return theme.COLOR_STATUS_GREEN_TEXT;
    case statusEnum.NEW:
    case statusEnum.PENDING:
      return '#0061FF';
    case statusEnum.IN_PROGRESS:
    case statusEnum.WAITING_CUSTOMER:
    case statusEnum.WAITING_SUPPLIER:
    case statusEnum.RESOLVED:
    case statusEnum.TEST_READY:
    case statusEnum.INVENTORY:
    case statusEnum.RETIRED:
    case statusEnum.RETIREMENT_REQUEST:
    case statusEnum.SENT:
    case statusEnum.UNVERIFIED:
    case statusEnum.PARTIALLY_FAILED:
    case statusEnum.SUSPENDED:
      return theme.COLOR_STATUS_ORANGE_TEXT;
    case statusEnum.ESCALATED:
    case statusEnum.ARCHIVE:
    case statusEnum.ARCHIVED:
    case statusEnum.DEACTIVATED:
    case statusEnum.DEACTIVATION_REQUEST:
    case statusEnum.DECLINED:
    case statusEnum.INVALID:
    case statusEnum.FAILED:
    case statusEnum.INACTIVE:
    case statusEnum.REJECTED:
      return theme.COLOR_STATUS_RED_TEXT;
    case statusEnum.IS_SUPPLIER:
      return theme.COLOR_SUPPLIER_BLUE_TEXT;
    case statusEnum.IS_DEALER:
    case statusEnum.QUOTED:
      return theme.COLOR_STATUS_PURPLE_TEXT;
    case statusEnum.DRAFT:
    default:
      return '#707172';
  }
};

/**
 * Get the background color for a status.
 *
 * @param string status.
 *
 * @returns string color.
 */

const getStatusColorBackground = (status) => {
  switch (status) {
    case statusEnum.ACTIVE:
    case statusEnum.ACTIVATED:
    case statusEnum.ACCEPTED:
    case statusEnum.ACTIVATION_READY:
    case statusEnum.ACTIVATION_REQUEST:
    case statusEnum.VERIFIED:
    case statusEnum.COMPLETED:
    case statusEnum.SOLD:
      return theme.COLOR_STATUS_GREEN_BG;
    case statusEnum.NEW:
    case statusEnum.PENDING:
      return '#BCD7F7';
    case statusEnum.IN_PROGRESS:
    case statusEnum.WAITING_CUSTOMER:
    case statusEnum.WAITING_SUPPLIER:
    case statusEnum.RESOLVED:
    case statusEnum.TEST_READY:
    case statusEnum.INVENTORY:
    case statusEnum.RETIRED:
    case statusEnum.RETIREMENT_REQUEST:
    case statusEnum.SENT:
    case statusEnum.UNVERIFIED:
    case statusEnum.SUSPENDED:
      return theme.COLOR_STATUS_ORANGE_BG;
    case statusEnum.ESCALATED:
    case statusEnum.ARCHIVE:
    case statusEnum.ARCHIVED:
    case statusEnum.DEACTIVATED:
    case statusEnum.DECLINED:
    case statusEnum.REJECTED:
    case statusEnum.FAILED:
    case statusEnum.INACTIVE:
      return theme.COLOR_STATUS_RED_BG;
    case statusEnum.IS_SUPPLIER:
      return theme.COLOR_SUPPLIER_BLUE_BG;
    case statusEnum.IS_DEALER:
    case statusEnum.QUOTED:
      return theme.COLOR_STATUS_PURPLE_BG;
    case statusEnum.DRAFT:
    default:
      return '#d8d8da';
  }
};

/**
 *
 * @param {object} props -
 * @param {string} props.status -
 * @param {object} props.containerStyle -
 * @returns {JSX.Element}
 */
const StatusIndicator = ({ status, containerStyle }) => (
  <Styled.Body
    bodycolor={getStatusColorBackground(status)}
    style={containerStyle}
  >
    <Styled.Text textcolor={getStatusColorText(status)}>
      {toSentenceCase(status?.replace(/_/g, ' '))}
    </Styled.Text>
  </Styled.Body>
);

StatusIndicator.defaultProps = {
  containerStyle: {},
};

StatusIndicator.propTypes = {
  status: PropTypes.string.isRequired,
  containerStyle: PropTypes.object,
};

export default StatusIndicator;
